/**
 * Modern Google Drive Service using Google Identity Services (GIS)
 * Uses the new authentication library instead of deprecated gapi.auth2
 */

import type { Character } from '../types/character';
import type {
  SaveResult,
  LoadResult,
  CharacterSummary,
  GoogleDriveAuth
} from '../types/persistence';
import { StorageLocation } from '../types/persistence';
import { GOOGLE_DRIVE_CONFIG } from '../config/google-drive';

// Google Identity Services types
interface GoogleTokenResponse {
  access_token: string;
  expires_in?: number;
  error?: string;
}

interface GoogleTokenClient {
  callback: (response: GoogleTokenResponse) => void;
  requestAccessToken: (options?: { prompt?: string }) => void;
}

interface GoogleOAuth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: GoogleTokenResponse) => void;
  }) => GoogleTokenClient;
  hasGrantedAllScopes: (token: GoogleTokenResponse, ...scopes: string[]) => boolean;
}

interface GoogleGapi {
  load: (apis: string, callback: () => void, errorCallback?: (error: unknown) => void) => void;
  client: {
    init: (config: { apiKey: string; discoveryDocs: string[] }) => Promise<void>;
    setToken: (token: { access_token: string }) => void;
    drive: {
      files: {
        list: (params: Record<string, unknown>) => Promise<{ result: { files?: GoogleApiFile[] } }>;
        create: (params: Record<string, unknown>) => Promise<{ result: { id: string } }>;
        get: (params: {
          fileId: string;
          alt?: string;
        }) => Promise<{ body: string; result: { id: string } }>;
      };
    };
    request: (params: {
      path: string;
      method: string;
      params?: Record<string, unknown>;
      headers?: Record<string, string>;
      body?: string;
    }) => Promise<{ result: { id: string } }>;
  };
}

interface GoogleApiFile {
  id: string;
  name: string;
  modifiedTime: string;
  size: string;
}

declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: GoogleOAuth2;
      };
    };
    gapi: GoogleGapi;
  }
}

export class GoogleDriveService {
  private gapi: GoogleGapi | null = null;
  private tokenClient: GoogleTokenClient | null = null;
  private accessToken: string | null = null;
  private tokenExpiresAt: Date | null = null;
  private isInitialized = false;
  private appFolderId: string | null = null;
  private readonly AUTH_STORAGE_KEY = 'google_drive_auth_token';
  private readonly AUTH_EXPIRY_KEY = 'google_drive_auth_expiry';

  // Public getter for initialization status
  get initialized(): boolean {
    return this.isInitialized;
  }

  constructor() {
    // Restore authentication state from localStorage
    this.restoreAuthState();

    // Start initialization but don't await it in constructor
    this.initializeServices().catch((error) => {
      console.error('❌ Modern Google Drive service initialization failed:', error);
    });
  }

  /**
   * Restore authentication state from localStorage
   */
  private restoreAuthState(): void {
    try {
      const storedToken = localStorage.getItem(this.AUTH_STORAGE_KEY);
      const storedExpiry = localStorage.getItem(this.AUTH_EXPIRY_KEY);

      if (storedToken && storedExpiry) {
        const expiryDate = new Date(storedExpiry);

        // Check if token is still valid (with 5 minute buffer)
        const now = new Date();
        const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds

        if (expiryDate.getTime() > now.getTime() + bufferTime) {
          this.accessToken = storedToken;
          this.tokenExpiresAt = expiryDate;
          console.log('✅ Restored valid Google Drive authentication from storage');
        } else {
          // Token expired, clear it
          this.clearAuthState();
          console.log('🔄 Stored Google Drive token expired, cleared from storage');
        }
      }
    } catch (error) {
      console.warn('Failed to restore Google Drive auth state:', error);
      this.clearAuthState();
    }
  }

  /**
   * Save authentication state to localStorage
   */
  private saveAuthState(): void {
    try {
      if (this.accessToken && this.tokenExpiresAt) {
        localStorage.setItem(this.AUTH_STORAGE_KEY, this.accessToken);
        localStorage.setItem(this.AUTH_EXPIRY_KEY, this.tokenExpiresAt.toISOString());
        console.log('💾 Saved Google Drive authentication to storage');
      }
    } catch (error) {
      console.warn('Failed to save Google Drive auth state:', error);
    }
  }

  /**
   * Clear authentication state from localStorage
   */
  private clearAuthState(): void {
    try {
      localStorage.removeItem(this.AUTH_STORAGE_KEY);
      localStorage.removeItem(this.AUTH_EXPIRY_KEY);
      this.accessToken = null;
      this.tokenExpiresAt = null;
    } catch (error) {
      console.warn('Failed to clear Google Drive auth state:', error);
    }
  }

  /**
   * Initialize Google APIs and Identity Services
   */
  private async initializeServices(): Promise<void> {
    try {
      console.log('🔄 Starting modern Google API initialization...');

      // Load both Google API and Google Identity Services
      await Promise.all([this.loadGoogleAPIScript(), this.loadGoogleIdentityScript()]);

      // Initialize Google API client (without auth2)
      await this.initializeGoogleAPI();

      // Initialize Google Identity Services for OAuth
      await this.initializeGoogleIdentity();

      this.isInitialized = true;
      console.log('✅ Modern Google Drive API fully initialized');
    } catch (error) {
      console.error('❌ Modern Google API initialization failed:', error);
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Load Google API script
   */
  private loadGoogleAPIScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.gapi) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;

      const timeout = setTimeout(() => {
        reject(new Error('Timeout loading Google API script'));
      }, 10000);

      script.onload = () => {
        clearTimeout(timeout);
        console.log('✅ Google API script loaded');
        setTimeout(() => resolve(), 100);
      };

      script.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load Google API script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Load Google Identity Services script
   */
  private loadGoogleIdentityScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;

      const timeout = setTimeout(() => {
        reject(new Error('Timeout loading Google Identity Services script'));
      }, 10000);

      script.onload = () => {
        clearTimeout(timeout);
        console.log('✅ Google Identity Services script loaded');
        setTimeout(() => resolve(), 100);
      };

      script.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load Google Identity Services script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Google API client (without deprecated auth2)
   */
  private async initializeGoogleAPI(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      window.gapi.load('client', async () => {
        try {
          console.log('🔄 Initializing Google API client...');

          await window.gapi.client.init({
            apiKey: GOOGLE_DRIVE_CONFIG.apiKey,
            discoveryDocs: [GOOGLE_DRIVE_CONFIG.discoveryDoc]
          });

          this.gapi = window.gapi;
          console.log('✅ Google API client initialized');
          resolve();
        } catch (error) {
          console.error('❌ Failed to initialize Google API client:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Initialize Google Identity Services for OAuth
   */
  private async initializeGoogleIdentity(): Promise<void> {
    try {
      console.log('🔄 Initializing Google Identity Services...');

      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_DRIVE_CONFIG.clientId,
        scope: GOOGLE_DRIVE_CONFIG.scopes,
        callback: (response: GoogleTokenResponse) => {
          if (response.error) {
            console.error('❌ OAuth token error:', response.error);
            return;
          }

          this.accessToken = response.access_token;
          console.log('✅ OAuth token received');
        }
      });

      console.log('✅ Google Identity Services initialized');
    } catch (error) {
      console.error('❌ Failed to initialize Google Identity Services:', error);
      throw error;
    }
  }

  /**
   * Sign in using Google Identity Services
   */
  async signIn(): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        console.log('🔄 Service not initialized, initializing now...');
        await this.initializeServices();
      }

      if (this.accessToken) {
        console.log('✅ Already have access token');
        return true;
      }

      console.log('🔐 Starting OAuth flow with Google Identity Services...');

      return new Promise<boolean>((resolve) => {
        // Update callback to resolve promise
        this.tokenClient.callback = (response: GoogleTokenResponse) => {
          if (response.error) {
            console.error('❌ OAuth error:', response.error);
            resolve(false);
            return;
          }

          this.accessToken = response.access_token;

          // Calculate expiry time (tokens typically last 1 hour)
          const expiresIn = response.expires_in || 3600; // Default to 1 hour
          this.tokenExpiresAt = new Date(Date.now() + expiresIn * 1000);

          // Save to localStorage for persistence across page navigation
          this.saveAuthState();

          console.log('✅ Successfully signed in with Google Identity Services');
          resolve(true);
        };

        // Request access token
        this.tokenClient.requestAccessToken({ prompt: 'consent' });
      });
    } catch (error) {
      console.error('❌ Sign-in failed:', error);
      return false;
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    if (this.accessToken) {
      // Revoke the token by making a direct API call
      try {
        await fetch(`https://oauth2.googleapis.com/revoke?token=${this.accessToken}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        console.log('✅ Token revoked');
      } catch (error) {
        console.warn('Failed to revoke token:', error);
      }

      // Clear both memory and localStorage
      this.clearAuthState();
      console.log('✅ Signed out and cleared stored authentication');
    }
  }

  /**
   * Check if signed in
   */
  isSignedIn(): boolean {
    // Check if token exists and is not expired
    if (this.accessToken && this.tokenExpiresAt) {
      const now = new Date();
      if (now >= this.tokenExpiresAt) {
        // Token expired, clear it
        this.clearAuthState();
        return false;
      }
      return true;
    }
    return false;
  }

  /**
   * Get authentication state
   */
  getAuthState(): GoogleDriveAuth {
    // Check if token is expired
    if (this.accessToken && this.tokenExpiresAt) {
      const now = new Date();
      if (now >= this.tokenExpiresAt) {
        // Token expired, clear it
        this.clearAuthState();
      }
    }

    return {
      isSignedIn: !!this.accessToken,
      accessToken: this.accessToken || undefined,
      expiresAt: this.tokenExpiresAt || undefined
    };
  }

  /**
   * Set authorization header for API calls
   */
  private setAuthHeader(): void {
    // Check if token is still valid before using it
    if (this.isSignedIn() && this.accessToken) {
      this.gapi.client.setToken({
        access_token: this.accessToken
      });
    } else {
      throw new Error('No valid authentication token available. Please sign in again.');
    }
  }

  /**
   * Get or create the RogueTraderWebApp folder
   */
  private async getAppFolder(): Promise<string> {
    if (this.appFolderId) {
      return this.appFolderId;
    }

    try {
      // Search for existing folder
      const response = await this.gapi.client.drive.files.list({
        q: "name='RogueTraderWebApp' and mimeType='application/vnd.google-apps.folder' and trashed=false",
        fields: 'files(id,name)'
      });

      const folders = response.result.files || [];

      if (folders.length > 0) {
        // Folder exists
        this.appFolderId = folders[0].id;
        console.log('✅ Found existing RogueTraderWebApp folder:', this.appFolderId);
        return this.appFolderId;
      }

      // Create new folder
      console.log('📁 Creating RogueTraderWebApp folder...');
      const createResponse = await this.gapi.client.drive.files.create({
        resource: {
          name: 'RogueTraderWebApp',
          mimeType: 'application/vnd.google-apps.folder'
        },
        fields: 'id'
      });

      this.appFolderId = createResponse.result.id;
      console.log('✅ Created RogueTraderWebApp folder:', this.appFolderId);
      return this.appFolderId;
    } catch (error) {
      console.error('❌ Failed to get/create app folder:', error);
      throw error;
    }
  }

  /**
   * Save character to Google Drive
   */
  async save(character: Character): Promise<SaveResult> {
    try {
      if (!this.isSignedIn()) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Not signed in to Google Drive'
        };
      }

      console.log(`💾 Saving character ${character.name} to Google Drive...`);

      this.setAuthHeader();

      // Get or create the app folder
      const folderId = await this.getAppFolder();

      // Prepare file content
      const fileContent = {
        character,
        metadata: {
          version: 1,
          timestamp: new Date().toISOString(),
          schemaVersion: character.schemaVersion,
          appVersion: '1.0.0'
        }
      };

      const fileName = `rogue-trader-${character.id}.json`;

      // Check if file already exists in the app folder
      const existingFile = await this.findCharacterFile(character.id);

      if (existingFile) {
        // Update existing file
        await this.updateFile(existingFile.id, fileName, JSON.stringify(fileContent, null, 2));
      } else {
        // Create new file in the app folder
        await this.createFile(fileName, JSON.stringify(fileContent, null, 2), folderId);
      }

      console.log('✅ Character saved to Google Drive successfully');
      return {
        success: true,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('❌ Failed to save character to Google Drive:', error);
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Load character from Google Drive
   */
  async load(characterId: string): Promise<LoadResult> {
    try {
      if (!this.isSignedIn()) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Not signed in to Google Drive'
        };
      }

      console.log(`📂 Loading character ${characterId} from Google Drive...`);

      this.setAuthHeader();

      const file = await this.findCharacterFile(characterId);
      if (!file) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Character not found in Google Drive'
        };
      }

      const fileContent = await this.downloadFile(file.id);
      const parsedContent = JSON.parse(fileContent);
      const character = parsedContent.character;

      console.log(`✅ Character ${character.name} loaded from Google Drive`);
      return {
        success: true,
        character,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('❌ Failed to load character from Google Drive:', error);
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * List all characters in Google Drive
   */
  async list(): Promise<CharacterSummary[]> {
    try {
      if (!this.isSignedIn()) {
        return [];
      }

      console.log('📋 Listing characters from Google Drive...');

      this.setAuthHeader();

      // Get the app folder ID
      const folderId = await this.getAppFolder();

      const response = await this.gapi.client.drive.files.list({
        q: `name contains 'rogue-trader-' and name contains '.json' and '${folderId}' in parents and trashed=false`,
        fields: 'files(id,name,modifiedTime,size)'
      });

      const files = response.result.files || [];
      const summaries: CharacterSummary[] = [];

      for (const file of files) {
        try {
          const content = await this.downloadFile(file.id);
          const parsedContent = JSON.parse(content);
          const character = parsedContent.character;

          summaries.push({
            id: character.id,
            name: character.name,
            career: character.career?.name || 'Unknown',
            level: Math.floor((character.experience?.spent || 0) / 100) + 1,
            lastModified: new Date(file.modifiedTime),
            location: StorageLocation.GoogleDrive,
            syncStatus: {
              synced: true,
              pending: false,
              conflict: false,
              error: false,
              offline: false
            }
          });
        } catch (error) {
          console.warn(`Failed to parse character file ${file.name}:`, error);
        }
      }

      console.log(`✅ Found ${summaries.length} characters in Google Drive`);
      return summaries;
    } catch (error) {
      console.error('❌ Failed to list characters from Google Drive:', error);
      return [];
    }
  }

  // Helper methods (same as before but using modern auth)
  private async findCharacterFile(characterId: string): Promise<GoogleApiFile | null> {
    try {
      // Get the app folder ID
      const folderId = await this.getAppFolder();

      const response = await this.gapi.client.drive.files.list({
        q: `name = 'rogue-trader-${characterId}.json' and '${folderId}' in parents and trashed=false`,
        fields: 'files(id,name,modifiedTime,size)'
      });

      const files = response.result.files || [];
      return files.length > 0 ? files[0] : null;
    } catch (error) {
      console.error('Error finding character file:', error);
      return null;
    }
  }

  private async createFile(
    name: string,
    content: string,
    parentFolderId?: string
  ): Promise<{ result: { id: string } }> {
    const boundary = '-------314159265358979323846';
    const delimiter = '\r\n--' + boundary + '\r\n';
    const close_delim = '\r\n--' + boundary + '--';

    const metadata = {
      name: name,
      parents: parentFolderId ? [parentFolderId] : []
    };

    const multipartRequestBody =
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(metadata) +
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      content +
      close_delim;

    const request = this.gapi.client.request({
      path: 'https://www.googleapis.com/upload/drive/v3/files',
      method: 'POST',
      params: { uploadType: 'multipart' },
      headers: {
        'Content-Type': 'multipart/related; boundary="' + boundary + '"'
      },
      body: multipartRequestBody
    });

    return request;
  }

  private async updateFile(
    fileId: string,
    name: string,
    content: string
  ): Promise<{ result: { id: string } }> {
    const boundary = '-------314159265358979323846';
    const delimiter = '\r\n--' + boundary + '\r\n';
    const close_delim = '\r\n--' + boundary + '--';

    const metadata = {
      name: name
    };

    const multipartRequestBody =
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(metadata) +
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      content +
      close_delim;

    const request = this.gapi.client.request({
      path: 'https://www.googleapis.com/upload/drive/v3/files/' + fileId,
      method: 'PATCH',
      params: { uploadType: 'multipart' },
      headers: {
        'Content-Type': 'multipart/related; boundary="' + boundary + '"'
      },
      body: multipartRequestBody
    });

    return request;
  }

  private async downloadFile(fileId: string): Promise<string> {
    const response = await this.gapi.client.drive.files.get({
      fileId: fileId,
      alt: 'media'
    });

    return response.body;
  }

  /**
   * Get detailed initialization status for debugging
   */
  getInitializationStatus() {
    return {
      isInitialized: this.isInitialized,
      hasGapi: !!this.gapi,
      hasAccessToken: !!this.accessToken,
      gapiLoaded: !!window.gapi,
      googleIdentityLoaded: !!window.google?.accounts?.oauth2,
      hasTokenClient: !!this.tokenClient,
      config: {
        apiKey: GOOGLE_DRIVE_CONFIG.apiKey.substring(0, 10) + '...',
        clientId: GOOGLE_DRIVE_CONFIG.clientId.substring(0, 20) + '...',
        apiKeyValid: GOOGLE_DRIVE_CONFIG.apiKey.startsWith('AIza'),
        clientIdValid: GOOGLE_DRIVE_CONFIG.clientId.includes('.googleusercontent.com')
      }
    };
  }

  /**
   * Manually trigger initialization
   */
  async ensureInitialized(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    console.log('🔄 Manually triggering modern Google API initialization...');
    await this.initializeServices();
  }
}

// Export singleton instance
export const googleDriveService = new GoogleDriveService();
